import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { SelectField } from "@/lib/crm";
import { ContractStatus } from "@/lib/contracts";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";

// collegamento dal lato entità: ordine = origine del contratto; licenza = copertura; documento = link (stesse API della scheda contratto)
const ATTACH = {
  order: (cid, id) => api.post(`/contracts/${cid}/order`, { order_id: id }),
  license: (cid, id) => api.post(`/contracts/${cid}/coverage`, { kind: "license", entity_id: id }),
  commercial_document: (cid, id) => api.post(`/contracts/${cid}/links`, { entity: "commercial_document", entity_id: id }),
};
const OPEN = ["bozza", "attivo", "sospeso", "in_scadenza", "scaduto"];

const AttachForm = ({ entity, id, companyId, linked, onDone }) => {
  const { can } = useAuth();
  const [opts, setOpts] = useState([]);
  const [cid, setCid] = useState("");
  useEffect(() => {
    api.get("/contracts", { params: { company_id: companyId } })
      .then((r) => setOpts(r.data.items.filter((c) => OPEN.includes(c.state) && !linked.includes(c.id) && !(entity === "order" && c.origin?.order_id))
        .map((c) => ({ key: c.id, label: `${c.number} · ${c.title}` })))).catch(() => setOpts([]));
  }, [entity, companyId, linked]);
  const save = async () => {
    try { await ATTACH[entity](cid, id); toast.success("Contratto collegato"); setCid(""); onDone(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 rounded-control bg-slate-50 p-3" data-testid={`contract-attach-${entity}`}>
      <SelectField label="Contratto esistente dello stesso cliente" value={cid} onChange={setCid} options={opts} placeholder={opts.length ? "Seleziona" : "Nessun contratto disponibile"} testId={`contract-attach-${entity}-select`} className="min-w-[16rem] flex-1" />
      <Button size="sm" disabled={!cid} onClick={save} data-testid={`contract-attach-${entity}-save`}>Collega</Button>
      {entity === "order" && can("contracts:create") && <Button size="sm" variant="outline" asChild data-testid="contract-create-from-order">
        <Link to={`/contratti?new=1&company_id=${companyId}&order_id=${id}`}>Crea contratto dall'ordine</Link></Button>}
    </div>
  );
};

// Reverse lookup: from asset / license / order / intervention / package / commercial document to the contracts that cover or link them.
export const LinkedContracts = ({ entity, id, companyId, bare }) => {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const load = useCallback(() => { if (can("contracts:read") && id) api.get("/contracts", { params: { entity, entity_id: id } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [entity, id, can]);
  useEffect(load, [load]);
  if (!can("contracts:read") || !items) return null;
  const attach = ATTACH[entity] && companyId && can("contracts:update") && (entity !== "order" || can("ordini:read"));
  const body = (
    <div data-testid={`linked-contracts-${entity}`}><SectionLabel>Contratti</SectionLabel>
      <ul className="mt-2 divide-y divide-line">{items.map((c) => <li key={c.id} className="flex items-center justify-between gap-2 py-2 text-sm">
        <Link to={`/contratti/${c.id}`} className="text-ink hover:underline" data-testid={`linked-contract-${c.id}`}>{c.number} · {c.title}</Link>
        <span className="flex items-center gap-2 text-xs text-slate-500">{c.end_date ? `fino al ${fmtDate(c.end_date)}` : ""}<ContractStatus value={c.state} /></span></li>)}
        {!items.length && <li className="py-2 text-sm text-slate-500">Nessun contratto collegato.</li>}</ul>
      {attach && <AttachForm entity={entity} id={id} companyId={companyId} linked={items.map((c) => c.id)} onDone={load} />}
    </div>);
  return bare ? body : <Panel className="p-5">{body}</Panel>;
};
